import PropTypes from 'prop-types'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import useAuth from '../hooks/useAuth'
import { loginRoute, safeDestination } from '../helpers/loginUrl'

/**
 * Client-side login gate around the routed page.
 *
 * It renders `children` (the page) only when no redirect is due, so a
 * signed-out visitor never mounts a page: a page that mounted first fired its
 * API calls, got 401s and raised "Something went wrong!" on the sign-in
 * screen.
 *
 * - Signed out, anywhere but /login: go to /login, remembering where the
 *   user was (or the destination they were already carrying) as `destUrl`.
 * - Signed out on /login: render the page (the sign-in screen).
 * - Signed in on /login: go to `destUrl`, or home.
 * - Signed in anywhere else with a `destUrl`: go there. This is the landing
 *   after admin-auth returns to the app root with `?destUrl=...`.
 * - Otherwise: render the page.
 *
 * Paths are router-relative, so any basename is added by the router.
 * `destUrl` is read through safeDestination, so a crafted value can never
 * send the user off the app. This is a usability gate, not access control:
 * every comms API call is authorised server-side.
 */
const AuthChecker = ({ children = null }) => {
    const { user } = useAuth()
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const destination = safeDestination(searchParams.get('destUrl'))
    const onLoginPage = location.pathname === '/login'

    if (!user?.email) {
        if (onLoginPage) return children
        return (
            <Navigate
                to={loginRoute(location.pathname, location.search)}
                replace
            />
        )
    }
    if (onLoginPage) return <Navigate to={destination || '/'} replace />
    if (destination) return <Navigate to={destination} replace />
    return children
}

AuthChecker.propTypes = {
    children: PropTypes.node,
}

export default AuthChecker
